import { canSwitchSocietyContext, normalizeRole } from '../constants/roles';

// Explicit data-scope model for the dashboard. The header always shows which
// of these is in effect so an operator can never confuse a global action with
// a society-scoped one (or vice versa).
export const SCOPE = Object.freeze({
  GLOBAL: 'GLOBAL_SCOPE',
  SOCIETY: 'SOCIETY_SCOPE',
  CITY: 'CITY_SCOPE',
  ASSIGNED: 'ASSIGNED_SCOPE',
});

// Routes whose data spans the whole platform. A society selector on these
// pages is misleading: the page never scopes to the active society, so we show
// an explicit scope chip instead of the selector.
export const GLOBAL_SCOPE_PATHS = Object.freeze([
  '/inventory/global',
  '/audit-logs',
  '/contact-inquiries',
  '/societies',
  '/onboarding',
]);

export const GLOBAL_SCOPE_LABEL = 'Global scope';
export const ASSIGNED_SCOPE_LABEL = 'Assigned sites';
export const NO_SOCIETY_LABEL = 'No society selected';

export const isGlobalScopePath = (pathname) => {
  if (typeof pathname !== 'string' || !pathname) {
    return false;
  }

  return GLOBAL_SCOPE_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));
};

// Resolves the effective data scope for the current page from the actor's role
// and route. The header uses this to decide whether a society selector is safe
// to show, whether to show a scope chip, and what scope text to display.
export const resolvePageScope = ({ role, pathname, activeSocietyName, fixedSocietyName } = {}) => {
  const normalizedRole = normalizeRole(role);
  const isGlobalPage = isGlobalScopePath(pathname);

  if (canSwitchSocietyContext(normalizedRole)) {
    if (isGlobalPage) {
      return {
        scope: SCOPE.GLOBAL,
        scopeLabel: GLOBAL_SCOPE_LABEL,
        societyName: null,
        isGlobalPage: true,
        showSocietySelector: false,
        showScopeChip: true,
      };
    }

    const name = activeSocietyName || '';
    return {
      scope: SCOPE.SOCIETY,
      scopeLabel: name || NO_SOCIETY_LABEL,
      societyName: name || null,
      isGlobalPage: false,
      showSocietySelector: true,
      showScopeChip: false,
    };
  }

  // Roles locked to a single society (society admin/manager/guard).
  if (fixedSocietyName) {
    return {
      scope: SCOPE.SOCIETY,
      scopeLabel: fixedSocietyName,
      societyName: fixedSocietyName,
      isGlobalPage,
      showSocietySelector: false,
      showScopeChip: false,
    };
  }

  // Field/desk roles that operate across assigned sites (technician, support).
  return {
    scope: SCOPE.ASSIGNED,
    scopeLabel: ASSIGNED_SCOPE_LABEL,
    societyName: null,
    isGlobalPage,
    showSocietySelector: false,
    showScopeChip: false,
  };
};
